import { Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useRecalledHere } from './answer-ref-context';
import { pulseField } from './field-reveal';
import { useBlockIdentity } from './fired-states';
import { FlowCardGlyph } from './flow-card-token';
import { FormulaWithValues } from './formula-field';
import { DRIVEN_ROW_NAME } from './scenario-stimulus';
import { useDrivenRowInForce } from './varies-with-section';
import { VersionSelector } from './version-selector';

/**
 * What a test resolved to: the version it is showing, the branch that matched, and the way into
 * editing either.
 *
 * ## One component, not one per block
 *
 * Every branching block needs this row and every banked block needs the pager in it, so writing
 * it per block is the experiment this repo has already run and lost: the branch carousel's own
 * doc records seven hand-rolled copies, three of which had quietly lost the armed delete, the
 * branch-jump hand-off and focus restoration. MCQ alone held two copies of this row — canvas and
 * phone — and they had already drifted apart on which element carried `w-full`.
 *
 * So the layout, the wrapping, the scope-tag handling and the Edit semantics live here once. A
 * block supplies what it resolved to; it does not get to decide how that reads.
 *
 * ## Layout follows content
 *
 * Two rows exist only to stop the pager being dragged around: the condition readout's width
 * changes with every roll — a formula, its value and a threshold, all variable-length — so
 * sharing a centred row with the chevrons moved them under the teacher's cursor. With no bank
 * there is no pager, nothing to protect, and a second row is a line of wasted height in a panel
 * that has none to spare, so a branching block with no versions keeps a single row.
 *
 * ## Edit is not only for branches
 *
 * It used to appear only once a branch had matched, so a teacher who stepped to a version with a
 * typo in its question had nothing to click: the way back was to leave the test, find the heading
 * pager and re-step to it there. A bank alone is reason enough. Where a branch DID match it is
 * carried too, because landing on the right version at the wrong branch is barely better than not
 * landing at all.
 */
export interface NoticeBranch {
  /** 0-based, as the block holds it; displayed 1-based. */
  index: number;
  total: number;
  /**
   * The condition itself, where the block can say it.
   *
   * Optional as a group: GPS reports WHICH branch matched but not what the equation came to, and
   * computing that just to fill this row would mean changing its evaluation path for a label. A
   * block that cannot say it shows "Branch 1 of 2" alone, which is what GPS already showed.
   */
  equation?: string;
  rolledValues?: Record<string, string>;
  /** What the equation evaluated to on this roll — already formatted by the block. */
  value?: React.ReactNode;
  operator?: string;
  threshold?: string;
  thresholdHigh?: string;
}

export function VersionBranchNotice({
  versions, activeVersionId, onSelectVersion, branch, onEdit, editScrollsTo, stacked = false, noun = 'version',
}: {
  /** The bank, or an empty array for a block that has none. A lone version is not a bank. */
  versions: { id: string }[];
  activeVersionId: string | undefined;
  /** Moves the TEST pointer. The editor's own pointer is only ever moved through `onEdit`. */
  onSelectVersion: (id: string) => void;
  branch: NoticeBranch | null;
  /** Opens the editor on the version AND branch this row is describing. */
  onEdit: () => void;
  /**
   * WHERE EDIT LANDS WHEN THERE IS NOTHING TO JUMP TO — a following block's scenario row.
   *
   * The pager and the editor share one pointer for scenarios, so stepping a scenario has already
   * moved the editor to that row: Edit has no pointer left to move and was, as the owner put it,
   * redundant. What is still useful is getting there, so it scrolls to the row instead.
   *
   * Deliberately NOT applied when a BRANCH matched. A branch is chosen by the roll, and an
   * editor that jumped every time the dice moved would be unusable — which is exactly why the
   * branch case keeps an explicit Edit that moves the editor on demand.
   */
  editScrollsTo?: { fieldId: string; blockId: string };
  /** Phone puts the condition's label on its own line above the formula. */
  stacked?: boolean;
  /**
   * What the pager is paging — passed straight to `VersionSelector`.
   *
   * A FOLLOWER hands this row the LEADER's bank: it has no versions of its own, and what its
   * test resolved to is a scenario. Same row, same pager, different noun — which is exactly
   * what this component exists to keep from being written twice.
   */
  noun?: 'version' | 'scenario';
}): React.ReactNode {
  const { t } = useTranslation();
  const hasBank = versions.length > 1;
  if (!hasBank && !branch) return undefined;

  const index = Math.max(0, versions.findIndex((v) => v.id === activeVersionId));

  const conditionLine = branch ? (
    <p className='text-center'>
      <span className={`font-medium text-ws-text whitespace-nowrap ${stacked ? 'block' : ''}`}>
        {t('blocks.branching.branchResolvedLabel', { current: branch.index + 1, total: branch.total })}
      </span>
      {branch.equation ? (
        <>
          {' '}
          <span className='text-ws-muted'>
            <FormulaWithValues formula={branch.equation} rolledValues={branch.rolledValues ?? {}} />
            {' → '}
            {branch.value}
            {' '}
            {branch.operator === 'between'
              ? `${t('blocks.branching.operatorBetween')} ${branch.threshold || '?'} ${t('blocks.connectors.to')} ${branch.thresholdHigh || '?'}`
              : `${branch.operator === '==' ? '=' : branch.operator} ${branch.threshold || '?'}`}
          </span>
        </>
      ) : null}
    </p>
  ) : null;

  const editButton = (
    <button
      type='button'
      onClick={() => {
        onEdit();
        if (!branch && editScrollsTo) void pulseField(editScrollsTo.fieldId, editScrollsTo.blockId);
      }}
      aria-label={branch
        ? (hasBank
          ? t('blocks.branching.editThisBranchInVersionA11y', {
            current: branch.index + 1, total: branch.total,
            version: index + 1, versions: versions.length,
          })
          : t('blocks.branching.editThisBranchA11y', { current: branch.index + 1, total: branch.total }))
        : t('blocks.versions.editThisVersionA11y', { current: index + 1, total: versions.length })}
      className='justify-self-end font-medium text-ws-primary hover:underline'
    >
      {branch ? t('blocks.branching.editThisBranch') : t('common.edit')}
    </button>
  );

  const box = 'rounded-md border border-ws-border bg-ws-surface px-3 py-1.5 text-sm';
  const row = 'grid grid-cols-[1fr_auto_1fr] items-center gap-2';

  if (!hasBank) {
    return <div className={`${box} ${row}`}><div />{conditionLine}{editButton}</div>;
  }

  return (
    <div className={`flex flex-col gap-1 ${box}`}>
      <div className={row}>
        <div />
        {/*
          * A PAGER, not a label. Reroll draws a version the way a student would, which is the
          * right default and useless for checking a bank: on a four-version block, confirming you
          * have seen all four takes an unbounded number of rolls. Stepping is the deliberate
          * pass, rerolling the realistic one, and a teacher needs both.
          *
          * Browse-only — no add, no delete. Looking at a version must not be able to destroy it.
          */}
        {/*
          * THE PAGER, OR THE ANSWER THAT REPLACED IT.
          *
          * On a driven block there is no pager: the earlier answer chooses the version, so
          * `VersionSelector` renders nothing. That left this three-column grid holding a
          * spacer, nothing, and an `Edit` pinned to the right of an empty bar — "why is
          * everything getting right aligned after your modifications?". The control that
          * DOES move the version belongs in the slot the one that used to is vacating, which
          * is also where the owner asked for it: "it and the edit button should be together
          * ... on the same row and next to each other".
          *
          * The test bar has no label beside it, so this is one of the two slots that says
          * the noun — see `VersionSelector`'s own note on the default.
          */}
        <DrivenVersionControl />
        <VersionSelector versions={versions} activeId={activeVersionId} onSelect={onSelectVersion} noun={noun} showNoun />
        {editButton}
      </div>
      {conditionLine}
    </div>
  );
}

/**
 * THE EARLIER ANSWER, AS THE THING THAT CHOOSES THE VERSION.
 *
 * It is in the recall row too, as one of the answers this block leans on — and that row is
 * prose: underlined text, because a quoted value reads as part of a sentence. This one is
 * not a quoted value. The owner: "if it's driven-by, that's actually a version ... it should
 * be visually distinct and not look like the other text cyclers."
 *
 * So it wears the pager's own clothes — a bordered control in the slot the pager occupies on
 * every other block — and the link glyph that marks a cross-task tie everywhere else. What a
 * teacher sees in this slot is "which version", however it is chosen.
 *
 * Nothing at all where the block is not driven, which is every block that still has a pager.
 */
function DrivenVersionControl() {
  const { t } = useTranslation();
  const identity = useBlockIdentity();
  const rows = useRecalledHere(identity?.blockId);
  const row = rows.find(r => r.driven);
  /*
   * THE ROW'S OWN WORDS WHERE THE ANSWER HAS NONE.
   *
   * A blank row's stand-in is an EMPTY answer, so it resolves to no text and this fell back to
   * the reference's NAME — the card read "Animal" while the editor's box, a few inches away,
   * read "No answer". Same fact, two spellings, which is the disagreement this control exists
   * to end.
   */
  const driven = useDrivenRowInForce();
  const named = driven && driven.row.kind !== 'option' ? t(DRIVEN_ROW_NAME[driven.row.kind]) : '';
  if (!row) return null;
  return (
    <button
      type='button'
      data-answer-ref-pick={row.aliasId}
      aria-label={t('blocks.answerRef.setRowCycleDriven',
        { name: row.name, answer: row.shown?.text || named })}
      className='flex items-center gap-1 rounded-md border border-ws-border bg-ws-btn px-2 py-0.5 text-sm text-ws-text hover:bg-ws-hover'
    >
      <Link2 aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-primary' />
      <span aria-hidden='true' className='max-w-[12rem] truncate'>
        {row.shown?.text || named || row.name}
      </span>
    </button>
  );
}

/** Re-exported so a block needs one import for the row and the glyph its conditions use. */
export { FlowCardGlyph };
